// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngSticky from "@awc/App/Components/XngSticky";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    isUpdatingCart: state.pages.checkoutShipping.isUpdatingCart,
});

interface OwnProps extends WidgetProps {}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CheckoutShippingPageContainerStyles {
    loadingOverlay?: LoadingOverlayProps;
    container?: GridContainerProps;
    headerGridItem?: GridItemProps;
    approverInfoGridItem?: GridItemProps;
    cartTotalGridItemNarrow?: GridItemProps;
    fulfillmentMethodAndAddressesGridItem?: GridItemProps;
    fulfillmentMethodAndAddressesContainer?: GridContainerProps;
    fulfillmentMethodAndNotesGridItem?: GridItemProps;
    fulfillmentMethodAndNotesContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    notesGridItemWide?: GridItemProps;
    addressesGridItem?: GridItemProps;
    notesAndCartTotalGridItem?: GridItemProps;
    notesAndCartTotalContainer?: GridContainerProps;
    notesGridItemNarrow?: GridItemProps;
    cartTotalGridItemWide?: GridItemProps;
    // XNG-Change
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
    addresses?: InjectableCss;
    shippingMethod?: InjectableCss;
    productList?: InjectableCss;
    paymentDetails?: InjectableCss;
}

export const containerStyles: CheckoutShippingPageContainerStyles = {
    loadingOverlay: {
        css: css`
            width: 100%;
        `,
    },
    headerGridItem: { width: 12 },
    approverInfoGridItem: {
        width: 12,
        css: css`
            padding: 0 15px;
        `,
    },
    cartTotalGridItemNarrow: { width: [12, 12, 0, 0, 0] },
    fulfillmentMethodAndAddressesGridItem: { width: [12, 12, 6, 8, 8] },
    fulfillmentMethodAndNotesGridItem: { width: 12 },
    fulfillmentMethodGridItem: { width: [12, 12, 12, 6, 6] },
    notesGridItemWide: { width: [0, 0, 0, 6, 6] },
    addressesGridItem: { width: 12 },
    notesAndCartTotalGridItem: { width: [12, 12, 6, 4, 4] },
    notesGridItemNarrow: { width: [12, 12, 12, 0, 0] },
    cartTotalGridItemWide: { width: [0, 0, 12, 12, 12] },
};

const styles = containerStyles;

const CheckoutShippingPageContainer = ({ id }: Props) => {
    // XNG-Change: change structure to match design
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.headerGridItem}>
                <Zone zoneName="Content0" contentId={id} />
            </StyledWrapper>

            <StyledWrapper {...styles.leftSection}>
                <StyledWrapper {...styles.addresses}>
                    <Zone zoneName="Content1" contentId={id} />
                </StyledWrapper>
                <StyledWrapper {...styles.shippingMethod}>
                    <Zone zoneName="Content2" contentId={id} />
                </StyledWrapper>
                <StyledWrapper {...styles.paymentDetails}>
                    <Zone zoneName="Content3" contentId={id} />
                </StyledWrapper>
                <StyledWrapper {...styles.productList}>
                    <Zone zoneName="Content4" contentId={id} />
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.rightSection}>
                <XngSticky>
                    <Zone zoneName="Content5" contentId={id} />
                </XngSticky>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CheckoutShippingPageContainer),
    definition: {
        displayName: "Page Container",
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage"],
    },
};

export default widgetModule;
